import { useState, useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { useQuery } from '@tanstack/react-query'
import { forecastService, reportService } from '../services/api'
import { Spinner, Modal, ConfirmDialog, EmptyState } from '../components/ui'
import {
  TrendingUp, TrendingDown, BarChart2, Users, ShoppingCart,
  Calendar, RefreshCw, AlertTriangle, Info,
  Package, Search, CheckCircle, X, Flame, Archive,
  Plus, Pencil, Trash2, RotateCcw, Save, Ban, PartyPopper, Vote,
  ChevronDown, Megaphone,
} from 'lucide-react'

// ─── Constants ───────────────────────────────────────────────────────────────

// Revenue metric intentionally excluded — only volume/activity data shown to staff
const METRICS = [
  { value: 'orders',    label: 'Orders Placed',  icon: ShoppingCart, color: '#0e947a', unit: '' },
  { value: 'customers', label: 'New Customers',   icon: Users,        color: '#6366f1', unit: '' },
]

const PERIODS = [
  { value: 30,  label: '30 Days' },
  { value: 90,  label: '90 Days' },
  { value: 180, label: '180 Days' },
  { value: 365, label: '1 Year'  },
]

const HISTORY = [
  { value: 90,   label: '3 Months' },
  { value: 180,  label: '6 Months' },
  { value: 365,  label: '1 Year'   },
  { value: 730,  label: '2 Years'  },
  { value: 1825, label: '5 Years'  },
]

const GRANULARITY = [
  { value: 'day',     label: 'Daily'     },
  { value: 'week',    label: 'Weekly'    },
  { value: 'month',   label: 'Monthly'   },
  { value: 'quarter', label: 'Quarterly' },
]

// horizon is chosen in DAYS; convert to Prophet periods for the active granularity
const GRAN_STEP_DAYS = { day: 1, week: 7, month: 30, quarter: 91 }
const horizonToPeriods = (days, gran) => Math.max(1, Math.round(days / (GRAN_STEP_DAYS[gran] || 1)))

const STOCK_STATUS = {
  out_of_stock: { label: 'Out of Stock', color: 'text-red-400',   bg: 'bg-red-400/10',   border: 'border-red-400/30',   icon: X             },
  critical:     { label: 'Critical',     color: 'text-red-400',   bg: 'bg-red-400/10',   border: 'border-red-400/30',   icon: AlertTriangle  },
  low:          { label: 'Low Stock',    color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/30', icon: AlertTriangle  },
  healthy:      { label: 'Healthy',      color: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400/30', icon: CheckCircle    },
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtVal = (v, _unit) =>
  Number(v || 0).toLocaleString('en-ZA', { maximumFractionDigits: 0 })

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: '2-digit' }) : ''

const btn = 'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border'
const on  = 'bg-primary text-white border-primary'
const off = 'bg-surface-card border-surface-border text-gray-400 hover:text-gray-300'

// ─── Shared ApexCharts config builder ────────────────────────────────────────

function buildChart(data, color, unit, events) {
  if (!data?.forecast) return { options: {}, series: [] }
  const { forecast, cutoff_date } = data
  const actuals     = forecast.filter(f => !f.is_future && f.actual !== null)
  const futureItems = forecast.filter(f => f.is_future)

  // Event annotations (holidays, voting day, bans, liquor events…) within the charted range
  const _from = forecast.length ? String(forecast[0].date).slice(0, 10) : null
  const _to   = forecast.length ? String(forecast[forecast.length - 1].date).slice(0, 10) : null
  const eventAnns = (events && _from && _to)
    ? eventsInRange(events, _from, _to).map(e => {
        const c = catMeta(e.category).color
        return {
          x: new Date(e.occ).getTime(),
          borderColor: c, borderWidth: 1, strokeDashArray: 3, opacity: 0.85,
          label: {
            text: e.name && e.name.length > 22 ? e.name.slice(0, 21) + '…' : (e.name || 'Event'),
            orientation: 'vertical', position: 'top', textAnchor: 'end',
            style: { color: '#0b0b0b', background: c, fontSize: '9px',
                     fontFamily: 'Exo 2, sans-serif', padding: { left: 4, right: 4, top: 1, bottom: 1 } },
          },
        }
      })
    : []

  const options = {
    chart: {
      type: 'line', background: 'transparent',
      toolbar: { show: true, tools: { download: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } },
      zoom: { enabled: true, type: 'x' },
      animations: { enabled: true, speed: 500 },
    },
    theme: { mode: 'dark' },
    stroke: { curve: 'smooth', width: [2, 2, 1, 1], dashArray: [0, 6, 0, 0] },
    colors: [color, color, `${color}40`, `${color}40`],
    fill: {
      type: ['solid', 'solid', 'gradient', 'gradient'],
      gradient: { shade: 'dark', type: 'vertical', opacityFrom: 0.28, opacityTo: 0.04 },
    },
    xaxis: {
      type: 'datetime',
      labels: { style: { colors: '#6b7280', fontSize: '11px' }, datetimeFormatter: { year: 'yyyy', month: "MMM 'yy", day: 'dd MMM' } },
      axisBorder: { show: false }, axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: '#6b7280', fontSize: '11px' }, formatter: v => fmtVal(v, unit) } },
    grid: { borderColor: '#1f1f1f', strokeDashArray: 4, xaxis: { lines: { show: false } } },
    tooltip: { theme: 'dark', x: { format: 'dd MMM yyyy' }, y: { formatter: v => fmtVal(v, unit) }, shared: true, intersect: false },
    legend: { show: true, position: 'top', horizontalAlign: 'right', labels: { colors: '#9ca3af' }, markers: { width: 10, height: 10, radius: 3 } },
    annotations: {
      xaxis: [
        {
          x: new Date(cutoff_date).getTime(),
          borderColor: '#374151', borderWidth: 1, strokeDashArray: 4,
          label: { text: 'Forecast Start', style: { color: '#9ca3af', background: '#1f1f1f', fontSize: '11px', fontFamily: 'Exo 2, sans-serif' }, position: 'top' },
        },
        ...eventAnns,
      ],
    },
    markers: { size: 0 },
  }

  const series = [
    { name: 'Actual',      data: actuals.map(f     => [new Date(f.date).getTime(), f.actual]),     type: 'line' },
    { name: 'Forecast',    data: forecast.map(f    => [new Date(f.date).getTime(), f.yhat]),        type: 'line' },
    { name: 'Upper Bound', data: futureItems.map(f => [new Date(f.date).getTime(), f.yhat_upper]), type: 'area' },
    { name: 'Lower Bound', data: futureItems.map(f => [new Date(f.date).getTime(), f.yhat_lower]), type: 'area' },
  ]
  return { options, series }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, icon: Icon, color, trend }) {
  const up = trend >= 0
  return (
    <div className="bg-surface-card border border-surface-border rounded-xl p-4 flex items-start gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
        <Icon size={18} style={{ color }}/>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-gray-400 text-xs mb-0.5">{label}</p>
        <p className="text-white font-bold text-lg leading-none">{value}</p>
        {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg ${up ? 'bg-green-400/10 text-green-400' : 'bg-red-400/10 text-red-400'}`}>
          {up ? <TrendingUp size={12}/> : <TrendingDown size={12}/>} {Math.abs(trend)}%
        </div>
      )}
    </div>
  )
}

function pad2(n) { return String(n).padStart(2, '0') }

// dd/mm/yyyy <-> ISO (yyyy-mm-dd) helpers for the event-date field
function isoToDisplay(iso) {
  const m = iso && String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : ''
}
function displayToIso(disp) {
  const m = disp && String(disp).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (!m) return ''
  const d = pad2(m[1]), mo = pad2(m[2]), y = m[3]
  const dt = new Date(`${y}-${mo}-${d}T00:00:00`)           // reject impossible dates (e.g. 31/02)
  if (dt.getUTCMonth() + 1 !== Number(mo) || dt.getUTCDate() !== Number(d)) return ''
  return `${y}-${mo}-${d}`
}

// Which calendar events actually occur within [from, to] (resolves annual + one-off).
function eventsInRange(events, from, to) {
  if (!events || !from || !to) return []
  const y0 = Number(from.slice(0, 4)), y1 = Number(to.slice(0, 4))
  const out = []
  for (const e of events) {
    if (e.is_active === false) continue
    let occ = []
    if (e.recurrence === 'one_off' && e.event_date) {
      occ = [String(e.event_date).slice(0, 10)]
    } else if (e.month && e.day) {
      for (let y = y0; y <= y1; y++) occ.push(`${y}-${pad2(e.month)}-${pad2(e.day)}`)
    }
    for (const d of occ) if (d >= from && d <= to) out.push({ ...e, occ: d })
  }
  return out.sort((a, b) => a.occ.localeCompare(b.occ))
}

function DrillDown({ forecast, unit }) {
  const future = forecast.filter(f => f.is_future)
  const [page, setPage] = useState(0)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  const { data: events } = useQuery({
    queryKey: ['forecast-events'],
    queryFn: () => forecastService.listEvents(),
    staleTime: 5 * 60 * 1000,
  })

  if (!future.length) return null
  const minDate = future[0].date
  const maxDate = future[future.length - 1].date
  const lo = from || minDate
  const hi = to || maxDate

  const rows = future.filter(f => f.date >= lo && f.date <= hi)
  const total = rows.reduce((s, r) => s + r.yhat, 0)
  const avg = rows.length ? total / rows.length : 0
  const peak = rows.reduce((m, r) => (r.yhat > (m ? m.yhat : -Infinity) ? r : m), null)
  const evs = eventsInRange(events, lo, hi)
  const narrowed = (from && from !== minDate) || (to && to !== maxDate)

  const PER = 14
  const pages = Math.max(1, Math.ceil(rows.length / PER))
  const pg = Math.min(page, pages - 1)
  const slice = rows.slice(pg * PER, (pg + 1) * PER)

  return (
    <div className="space-y-3">
      {/* Range picker + summary */}
      <div className="bg-surface-card border border-surface-border rounded-xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <BarChart2 size={14} className="text-primary"/> Drill into a period
          </h3>
          <div className="flex items-center gap-2 text-xs">
            <input type="date" value={from || minDate} min={minDate} max={maxDate}
              onChange={e => { setFrom(e.target.value); setPage(0) }}
              className="bg-black/40 border border-surface-border rounded-lg px-2 py-1 text-gray-300 outline-none focus:border-primary"/>
            <span className="text-gray-500">to</span>
            <input type="date" value={to || maxDate} min={minDate} max={maxDate}
              onChange={e => { setTo(e.target.value); setPage(0) }}
              className="bg-black/40 border border-surface-border rounded-lg px-2 py-1 text-gray-300 outline-none focus:border-primary"/>
            {narrowed && (
              <button onClick={() => { setFrom(''); setTo(''); setPage(0) }}
                className="px-2 py-1 rounded-lg border border-surface-border text-gray-400 hover:text-white">Reset</button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Days', value: rows.length },
            { label: 'Total', value: fmtVal(total, unit) },
            { label: 'Avg / day', value: fmtVal(avg, unit) },
            { label: 'Peak day', value: peak ? fmtVal(peak.yhat, unit) : '—', sub: peak ? fmtDate(peak.date) : '' },
          ].map(x => (
            <div key={x.label} className="bg-black/30 rounded-lg px-3 py-2">
              <p className="text-[11px] text-gray-500">{x.label}</p>
              <p className="text-white font-semibold text-sm">{x.value}</p>
              {x.sub && <p className="text-[10px] text-gray-500">{x.sub}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Events driving this period */}
      <div className="bg-surface-card border border-surface-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-surface-border">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Calendar size={14} className="text-primary"/> Events in this period
            <span className="text-xs text-gray-500 font-normal">({evs.length})</span>
          </h3>
        </div>
        {evs.length === 0 ? (
          <p className="px-4 py-4 text-xs text-gray-500">No calendar events fall in this range — the forecast here reflects baseline demand and weekly seasonality.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="admin-thead border-b border-[#2a2a2a]">
                  {['Date','Event','Category','Window'].map(h => (
                    <th key={h} className="text-left px-4 py-2 text-gray-400 text-xs font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {evs.map((e, i) => {
                  const m = catMeta(e.category)
                  const Icon = m.icon
                  return (
                    <tr key={`${e.id}-${e.occ}`} className={`border-b border-surface-border/40 last:border-0 ${i%2===0?'':'bg-white/[0.02]'}`}>
                      <td className="px-4 py-2.5 text-gray-300 font-medium">{fmtDate(e.occ)}</td>
                      <td className="px-4 py-2.5 text-white">{e.name}</td>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                          style={{ background: `${m.color}1a`, color: m.color }}>
                          <Icon size={11}/> {m.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">
                        {(e.lower_window || e.upper_window)
                          ? `${e.lower_window || 0} to +${e.upper_window || 0} days`
                          : 'same day'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Forecast detail rows (within the selected range) */}
      <div className="bg-surface-card border border-surface-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-surface-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2"><BarChart2 size={14} className="text-primary"/> Forecast Detail</h3>
          {pages > 1 && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <button onClick={() => setPage(p => Math.max(0,p-1))} disabled={pg===0} className="px-2 py-1 rounded border border-surface-border disabled:opacity-40 hover:bg-white/5">&larr;</button>
              <span>{pg+1}/{pages}</span>
              <button onClick={() => setPage(p => Math.min(pages-1,p+1))} disabled={pg===pages-1} className="px-2 py-1 rounded border border-surface-border disabled:opacity-40 hover:bg-white/5">&rarr;</button>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="admin-thead border-b border-[#2a2a2a]">
                {['Date','Low (80%)','Forecast','High (80%)','Confidence'].map(h => (
                  <th key={h} className={`px-4 py-2 text-gray-400 text-xs font-medium ${h==='Date'?'text-left':'text-right'} last:text-left`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slice.map((row, i) => {
                const range = row.yhat_upper - row.yhat_lower
                const conf  = row.yhat > 0 ? Math.min(100, Math.max(0, Math.round(100 - (range / (row.yhat * 2)) * 100))) : 0
                return (
                  <tr key={row.date} className={`border-b border-surface-border/40 last:border-0 ${i%2===0?'':'bg-white/[0.02]'}`}>
                    <td className="px-4 py-2.5 text-gray-300 font-medium">{fmtDate(row.date)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-500 text-xs">{fmtVal(row.yhat_lower, unit)}</td>
                    <td className="px-4 py-2.5 text-right text-white font-semibold">{fmtVal(row.yhat, unit)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-500 text-xs">{fmtVal(row.yhat_upper, unit)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${conf}%`, background: conf>70?'#0e947a':conf>40?'#d4af37':'#ef4444' }}/>
                        </div>
                        <span className="text-xs text-gray-500 w-8 text-right">{conf}%</span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function HolidaysPanel({ holidays }) {
  if (!holidays || holidays.length === 0) return null
  return (
    <div className="bg-surface-card border border-surface-border rounded-xl p-4">
      <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
        <Calendar size={14} className="text-gold"/> SA Holidays in Forecast Window
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {holidays.map((h, i) => {
          const isBan = h.name.toLowerCase().includes('ban')
          return (
            <div key={i} className={`rounded-lg px-3 py-2 border ${isBan ? 'bg-red-400/10 border-red-400/20' : 'bg-yellow-400/5 border-yellow-400/15'}`}>
              <p className={`text-xs font-semibold ${isBan ? 'text-red-400' : 'text-yellow-400'}`}>{h.name}</p>
              <p className="text-gray-400 text-xs mt-0.5">{fmtDate(h.date)}</p>
              {isBan && <p className="text-red-500 text-[10px] mt-0.5">⚠ Alcohol ban — expect zero sales</p>}
            </div>
          )
        })}
      </div>
      <p className="text-gray-600 text-xs mt-3 flex items-center gap-1.5">
        <Info size={11}/> Holiday spikes and alcohol bans are baked into the forecast model — demand is adjusted automatically around each date.
      </p>
    </div>
  )
}

function StockRunway({ stock }) {
  if (!stock) return null
  const st = STOCK_STATUS[stock.stock_status] || STOCK_STATUS.healthy
  const Icon = st.icon
  const pct = stock.days_until_stockout
    ? Math.min(100, Math.round((stock.days_until_stockout / 90) * 100))
    : 100
  return (
    <div className={`bg-surface-card border ${st.border} rounded-xl p-4`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Archive size={14} className={st.color}/> Stock Runway</h3>
        <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${st.bg} ${st.color} border ${st.border}`}>
          <Icon size={11}/> {st.label}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[
          { label: 'Current Stock',      value: `${stock.current_level.toLocaleString()} units`,         icon: Archive,      color: '#6b7280' },
          { label: 'Avg Daily Demand',   value: `${stock.avg_daily_demand} units/day`,                   icon: TrendingUp,   color: '#0e947a' },
          { label: 'Forecasted Demand',  value: `${Math.round(stock.forecasted_demand_total)} units`,    icon: BarChart2,    color: '#d4af37' },
          { label: 'Days to Reorder',    value: stock.days_until_reorder ? `~${stock.days_until_reorder}d` : 'Not needed', icon: AlertTriangle, color: '#f59e0b' },
        ].map(({ label, value, icon: I, color }) => (
          <div key={label} className="bg-black/20 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-1"><I size={12} style={{ color }}/><p className="text-gray-500 text-xs">{label}</p></div>
            <p className="text-white font-semibold text-sm">{value}</p>
          </div>
        ))}
      </div>
      <div>
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Stock runway</span>
          <span>{stock.days_until_stockout ? `Stockout in ~${stock.days_until_stockout} days` : 'No stockout in forecast window'}</span>
        </div>
        <div className="h-2.5 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: pct < 20 ? '#ef4444' : pct < 50 ? '#f59e0b' : '#0e947a' }}/>
        </div>
      </div>
    </div>
  )
}

function ProductSelector({ selected, onSelect }) {
  const [search, setSearch] = useState('')
  const { data, isLoading } = useQuery({
    queryKey: ['forecast', 'top-products'],
    queryFn: () => forecastService.getTopProducts(25),
    staleTime: 5 * 60 * 1000,
  })
  const products = Array.isArray(data) ? data : []
  const filtered = products.filter(p =>
    !search ||
    p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.stock_code?.toLowerCase().includes(search.toLowerCase()) ||
    p.brand?.toLowerCase().includes(search.toLowerCase())
  )
  return (
    <div className="bg-surface-card border border-surface-border rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-white flex-1">Select Product</h3>
        <span className="text-xs text-gray-500">Top {products.length} by volume</span>
      </div>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, code or brand…"
          className="w-full bg-black/40 border border-[#323232] rounded-xl pl-8 pr-3 py-2 text-white text-sm focus:outline-none focus:border-primary/60 placeholder:text-gray-600"/>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-6"><Spinner/></div>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto space-y-1 pr-1">
          {filtered.length === 0 && <p className="text-gray-500 text-sm text-center py-4">No products found</p>}
          {filtered.map(p => {
            const sc = p.stock_level <= 0 ? 'text-red-400' : p.stock_level < 20 ? 'text-amber-400' : 'text-green-400'
            return (
              <button key={p.id} onClick={() => onSelect(p)}
                className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors ${selected?.id===p.id?'bg-primary/10 border-primary/30 text-primary':'border-transparent hover:bg-white/5 text-gray-300'}`}>
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {p.primary_image ? <img src={p.primary_image} alt="" className="w-full h-full object-cover"/> : <Package size={14} className="text-gray-500"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-xs truncate">{p.name}</p>
                  <p className="text-gray-500 text-xs">{p.stock_code}{p.brand ? ` · ${p.brand}` : ''}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-xs font-semibold ${sc}`}>{p.stock_level} units</p>
                  <p className="text-gray-600 text-xs">{p.units_sold} sold</p>
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Business Metrics Tab ─────────────────────────────────────────────────────

function BusinessTab() {
  const [metric, setMetric]           = useState('orders')
  const [periods, setPeriods]         = useState(90)
  const [historyDays, setHistoryDays] = useState(180)
  const [granularity, setGranularity] = useState('day')
  const [shownEvents, setShownEvents] = useState(new Set())
  const toggleEvent = (id) => setShownEvents(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  const meta = METRICS.find(m => m.value === metric)

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['forecast', 'biz', metric, periods, historyDays, granularity],
    queryFn: () => forecastService.get({ metric, periods: horizonToPeriods(periods, granularity), history_days: historyDays, granularity }),
    staleTime: 5 * 60 * 1000, retry: 1,
  })

  const { data: eventsData } = useQuery({
    queryKey: ['forecast', 'events'],
    queryFn: () => forecastService.listEvents(),
    staleTime: 5 * 60 * 1000,
  })
  const events = Array.isArray(eventsData) ? eventsData : (eventsData?.items ?? [])

  const { options, series } = useMemo(() => {
    const shown = shownEvents.size === 0 ? events : events.filter(e => shownEvents.has(e.id))
    return buildChart(data, meta.color, meta.unit, shown)
  }, [data, meta, events, shownEvents])
  const summary = data?.summary

  return (
    <div className="space-y-5">
      <div className="bg-surface-card border border-surface-border rounded-xl p-4 space-y-4">
        <div>
          <p className="text-xs text-gray-400 mb-2 font-medium">Metric</p>
          <div className="flex gap-2 flex-wrap">
            {METRICS.map(m => { const I = m.icon; return (
              <button key={m.value} onClick={() => setMetric(m.value)} className={`flex items-center gap-1.5 ${btn} ${metric===m.value?on:off}`}><I size={13}/> {m.label}</button>
            )})}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-gray-400 mb-2 font-medium">Forecast Horizon</p>
            <div className="flex gap-1.5 flex-wrap items-center">
              {PERIODS.map(p => <button key={p.value} onClick={() => setPeriods(p.value)} className={`${btn} ${periods===p.value?on:off}`}>{p.label}</button>)}
              <input type="number" min="1" value={periods} onChange={e => setPeriods(Math.max(1, parseInt(e.target.value) || 1))} title="Custom (days)"
                className="w-16 bg-black/40 border border-[#323232] rounded-lg px-2 py-1 text-white text-xs focus:outline-none focus:border-primary/60"/>
              <span className="text-[10px] text-gray-500">days</span>
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-2 font-medium">Training History</p>
            <div className="flex gap-1.5 flex-wrap items-center">
              {HISTORY.map(h => <button key={h.value} onClick={() => setHistoryDays(h.value)} className={`${btn} ${historyDays===h.value?on:off}`}>{h.label}</button>)}
              <input type="number" min="7" value={historyDays} onChange={e => setHistoryDays(Math.max(7, parseInt(e.target.value) || 7))} title="Custom (days)"
                className="w-16 bg-black/40 border border-[#323232] rounded-lg px-2 py-1 text-white text-xs focus:outline-none focus:border-primary/60"/>
              <span className="text-[10px] text-gray-500">days</span>
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-2 font-medium">Granularity</p>
            <div className="flex gap-1.5 flex-wrap">{GRANULARITY.map(g => <button key={g.value} onClick={() => setGranularity(g.value)} className={`${btn} ${granularity===g.value?on:off}`}>{g.label}</button>)}</div>
          </div>
        </div>
        {events.length > 0 && (
          <div>
            <p className="text-xs text-gray-400 mb-2 font-medium">Events shown on chart <span className="text-gray-600">(none selected = show all)</span></p>
            <div className="flex gap-1.5 flex-wrap max-h-24 overflow-y-auto">
              {events.map(e => (
                <button key={e.id} onClick={() => toggleEvent(e.id)} className={`${btn} ${shownEvents.has(e.id)?on:off} flex items-center gap-1.5`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: catMeta(e.category).color }}/> {e.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label={`Forecasted (${periods}d)`} value={fmtVal(summary.forecast_total, meta.unit)} icon={meta.icon} color={meta.color} trend={summary.growth_pct}/>
          <KpiCard label="Same Period Prior" value={fmtVal(summary.last_period_total, meta.unit)} icon={Calendar} color="#6b7280"/>
          <KpiCard label="Projected Growth" value={`${summary.growth_pct>0?'+':''}${summary.growth_pct}%`} sub="vs equivalent prior period" icon={summary.growth_pct>=0?TrendingUp:TrendingDown} color={summary.growth_pct>=0?'#10b981':'#ef4444'}/>
          <KpiCard label="Training Points" value={summary.data_points} sub={`${historyDays} days history`} icon={BarChart2} color="#8b5cf6"/>
        </div>
      )}

      <div className="bg-surface-card border border-surface-border rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-white">{meta.label} Forecast — SA Holiday Aware</h2>
          <span className="text-xs text-gray-500 flex items-center gap-1"><Info size={11}/> 80% confidence band</span>
        </div>
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-72 gap-3"><Spinner size="lg"/><p className="text-gray-500 text-sm">Training with SA holidays…</p></div>
        ) : isError ? (
          <div className="flex flex-col items-center justify-center h-72 gap-3">
            <AlertTriangle size={32} className="text-amber-400"/>
            <p className="text-gray-400 text-sm text-center max-w-sm">{error?.response?.data?.detail || 'Not enough historical data.'}</p>
            <button onClick={() => refetch()} className="btn-secondary text-xs">Retry</button>
          </div>
        ) : series.length > 0 ? (
          <ReactApexChart options={options} series={series} type="line" height={360}/>
        ) : null}
      </div>

      {!isLoading && !isError && data?.forecast && <DrillDown forecast={data.forecast} unit={meta.unit}/>}
    </div>
  )
}

// ─── Product Demand Tab ───────────────────────────────────────────────────────

function ProductTab() {
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [periods, setPeriods]                 = useState(90)
  const [historyDays, setHistoryDays]         = useState(180)
  const [granularity, setGranularity]         = useState('day')
  const [shownEvents, setShownEvents]         = useState(new Set())
  const toggleEvent = (id) => setShownEvents(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['forecast', 'product', selectedProduct?.id, periods, historyDays, granularity],
    queryFn: () => forecastService.getProduct({ product_id: selectedProduct.id, periods: horizonToPeriods(periods, granularity), history_days: historyDays, granularity }),
    enabled: !!selectedProduct,
    staleTime: 5 * 60 * 1000, retry: 1,
  })

  const { data: eventsData } = useQuery({
    queryKey: ['forecast', 'events'],
    queryFn: () => forecastService.listEvents(),
    staleTime: 5 * 60 * 1000,
  })
  const events = Array.isArray(eventsData) ? eventsData : (eventsData?.items ?? [])

  const { options, series } = useMemo(() => {
    const shown = shownEvents.size === 0 ? events : events.filter(e => shownEvents.has(e.id))
    return buildChart(data, '#0e947a', '', shown)
  }, [data, events, shownEvents])

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      {/* Product list */}
      <div><ProductSelector selected={selectedProduct} onSelect={setSelectedProduct}/></div>

      {/* Results panel */}
      <div className="lg:col-span-2 space-y-5">
        {/* Controls */}
        <div className="bg-surface-card border border-surface-border rounded-xl p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <p className="text-xs text-gray-400 mb-2 font-medium">Forecast Horizon</p>
              <div className="flex gap-1.5 flex-wrap items-center">
                {PERIODS.map(p => <button key={p.value} onClick={() => setPeriods(p.value)} className={`${btn} ${periods===p.value?on:off}`}>{p.label}</button>)}
                <input type="number" min="1" value={periods} onChange={e => setPeriods(Math.max(1, parseInt(e.target.value) || 1))} title="Custom (days)"
                  className="w-16 bg-black/40 border border-[#323232] rounded-lg px-2 py-1 text-white text-xs focus:outline-none focus:border-primary/60"/>
                <span className="text-[10px] text-gray-500">days</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-2 font-medium">Training History</p>
              <div className="flex gap-1.5 flex-wrap items-center">
                {HISTORY.map(h => <button key={h.value} onClick={() => setHistoryDays(h.value)} className={`${btn} ${historyDays===h.value?on:off}`}>{h.label}</button>)}
                <input type="number" min="7" value={historyDays} onChange={e => setHistoryDays(Math.max(7, parseInt(e.target.value) || 7))} title="Custom (days)"
                  className="w-16 bg-black/40 border border-[#323232] rounded-lg px-2 py-1 text-white text-xs focus:outline-none focus:border-primary/60"/>
                <span className="text-[10px] text-gray-500">days</span>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-2 font-medium">Granularity</p>
              <div className="flex gap-1.5 flex-wrap">{GRANULARITY.map(g => <button key={g.value} onClick={() => setGranularity(g.value)} className={`${btn} ${granularity===g.value?on:off}`}>{g.label}</button>)}</div>
            </div>
          </div>
          {events.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-gray-400 mb-2 font-medium">Events shown on chart <span className="text-gray-600">(none selected = show all)</span></p>
              <div className="flex gap-1.5 flex-wrap max-h-24 overflow-y-auto">
                {events.map(e => (
                  <button key={e.id} onClick={() => toggleEvent(e.id)} className={`${btn} ${shownEvents.has(e.id)?on:off} flex items-center gap-1.5`}>
                    <span className="w-2 h-2 rounded-full" style={{ background: catMeta(e.category).color }}/> {e.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {!selectedProduct && (
          <div className="bg-surface-card border border-surface-border rounded-xl flex flex-col items-center justify-center py-20 gap-3 text-center">
            <Package size={40} className="text-gray-600"/>
            <p className="text-gray-400 font-medium">Select a product to view demand forecast</p>
            <p className="text-gray-600 text-sm max-w-xs">Stock runway, SA holiday effects, and demand projections will appear here.</p>
          </div>
        )}

        {selectedProduct && isLoading && (
          <div className="bg-surface-card border border-surface-border rounded-xl flex flex-col items-center justify-center py-16 gap-3">
            <Spinner size="lg"/>
            <p className="text-gray-500 text-sm">Modelling demand with SA holiday effects…</p>
          </div>
        )}

        {selectedProduct && isError && (
          <div className="bg-surface-card border border-surface-border rounded-xl flex flex-col items-center justify-center py-12 gap-3">
            <AlertTriangle size={32} className="text-amber-400"/>
            <p className="text-gray-400 text-sm text-center max-w-sm">{error?.response?.data?.detail || 'Not enough sales history for this product.'}</p>
            <button onClick={() => refetch()} className="btn-secondary text-xs">Retry</button>
          </div>
        )}

        {selectedProduct && !isLoading && !isError && data && (<>
          <StockRunway stock={data.stock}/>
          <HolidaysPanel holidays={data.upcoming_holidays}/>
          <div className="bg-surface-card border border-surface-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-sm font-semibold text-white">{data.product.name}</h2>
                <p className="text-gray-500 text-xs mt-0.5">{data.product.stock_code}{data.product.brand ? ` · ${data.product.brand}` : ''} · Units Demand Forecast</p>
              </div>
              <span className="text-xs text-gray-500 flex items-center gap-1"><Info size={11}/> 80% confidence</span>
            </div>
            <ReactApexChart options={options} series={series} type="line" height={300}/>
          </div>
          <DrillDown forecast={data.forecast} unit=""/>
        </>)}
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'business', label: 'Business Metrics', icon: TrendingUp },
  { key: 'product',  label: 'Product Demand',   icon: Flame       },
  { key: 'calendar', label: 'Holiday Calendar', icon: Calendar    },
]

// ─── Holiday / voting-day calendar manager ───────────────────────────────────

const EVENT_CATEGORIES = [
  { value: 'public_holiday', label: 'Public Holiday', icon: Calendar,    color: '#6366f1' },
  { value: 'liquor_event',   label: 'Liquor Event',   icon: PartyPopper, color: '#0e947a' },
  { value: 'alcohol_ban',    label: 'Alcohol Ban',    icon: Ban,         color: '#ef4444' },
  { value: 'voting_day',     label: 'Voting Day',     icon: Vote,        color: '#d4af37' },
  { value: 'custom',         label: 'Custom',         icon: Flame,       color: '#94a3b8' },
]
const catMeta = (c) => EVENT_CATEGORIES.find(x => x.value === c) || EVENT_CATEGORIES[4]
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

const blankEvent = () => ({
  name: '', category: 'public_holiday', recurrence: 'annual_fixed',
  month: 1, day: 1, event_date: '', lower_window: -1, upper_window: 1,
  is_active: true, note: '',
})

function EventForm({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState(() => ({ ...blankEvent(), ...initial,
    event_date: initial?.event_date ? String(initial.event_date).slice(0, 10) : '' }))
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }))
  const submit = () => {
    if (!f.name.trim()) return
    if (f.recurrence === 'one_off' && !f.event_date) return   // need a date
    const payload = { ...f,
      month: f.recurrence === 'annual_fixed' ? Number(f.month) : null,
      day:   f.recurrence === 'annual_fixed' ? Number(f.day)   : null,
      event_date: f.recurrence === 'one_off' ? (f.event_date || null) : null,
      lower_window: Number(f.lower_window), upper_window: Number(f.upper_window),
    }
    onSave(payload)
  }
  const field = "w-full bg-black/40 border border-surface-border rounded-lg px-3 py-2 text-sm text-white focus:border-primary outline-none"
  const lbl = "block text-xs text-gray-400 mb-1"
  return (
    <div className="space-y-4">
      <div>
        <label className={lbl}>Name</label>
        <input className={field} value={f.name} onChange={e => set('name', e.target.value)}
          placeholder="e.g. Local Government Elections" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lbl}>Category</label>
          <select className={field} value={f.category} onChange={e => set('category', e.target.value)}>
            {EVENT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className={lbl}>Recurrence</label>
          <select className={field} value={f.recurrence} onChange={e => set('recurrence', e.target.value)}>
            <option value="annual_fixed">Every year (fixed date)</option>
            <option value="one_off">One-off (specific date)</option>
          </select>
        </div>
      </div>
      {f.recurrence === 'annual_fixed' ? (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Month</label>
            <select className={field} value={f.month} onChange={e => set('month', e.target.value)}>
              {MONTHS.map((m, i) => <option key={m} value={i+1}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className={lbl}>Day</label>
            <input type="number" min="1" max="31" className={field}
              value={f.day} onChange={e => set('day', e.target.value)} />
          </div>
        </div>
      ) : (
        <div>
          <label className={lbl}>Date</label>
          <input type="date" className={field}
            value={f.event_date} onChange={e => set('event_date', e.target.value)} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={lbl}>Window before (days)</label>
          <input type="number" className={field} value={f.lower_window}
            onChange={e => set('lower_window', e.target.value)} />
        </div>
        <div>
          <label className={lbl}>Window after (days)</label>
          <input type="number" className={field} value={f.upper_window}
            onChange={e => set('upper_window', e.target.value)} />
        </div>
      </div>
      <p className="text-xs text-gray-500 -mt-1">
        Windows spread the demand effect around the date. Use <span className="text-gray-300">0 / 0</span> for
        alcohol bans so the ban isn't extrapolated to nearby days.
      </p>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" checked={f.is_active}
            onChange={e => set('is_active', e.target.checked)} className="accent-primary" />
          Active (used in the forecast)
        </label>
      </div>
      <div>
        <label className={lbl}>Note (optional)</label>
        <input className={field} value={f.note || ''} onChange={e => set('note', e.target.value)} />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white border border-surface-border">Cancel</button>
        <button onClick={submit} disabled={saving || !f.name.trim()}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-white flex items-center gap-2 disabled:opacity-50">
          {saving ? <Spinner size="sm"/> : <Save size={14}/>} Save
        </button>
      </div>
    </div>
  )
}

function CalendarTab() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['forecast-events'],
    queryFn: () => forecastService.listEvents(),
  })
  const [modal, setModal] = useState(null)      // null | 'new' | event object (edit)
  const [confirm, setConfirm] = useState(null)  // event pending delete
  const [resetConfirm, setResetConfirm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const events = data || []
  const grouped = useMemo(() => {
    const g = {}
    for (const e of events) (g[e.category] ||= []).push(e)
    return g
  }, [events])

  const whenLabel = (e) => e.recurrence === 'annual_fixed'
    ? `${MONTHS[(e.month||1)-1]} ${e.day} · yearly`
    : (e.event_date || '—')

  const save = async (payload) => {
    setSaving(true); setErr('')
    try {
      if (modal === 'new') await forecastService.createEvent(payload)
      else await forecastService.updateEvent(modal.id, payload)
      setModal(null); refetch()
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed.')
    } finally { setSaving(false) }
  }
  const doDelete = async () => {
    try { await forecastService.deleteEvent(confirm.id); setConfirm(null); refetch() }
    catch { setConfirm(null) }
  }
  const doReset = async () => {
    try { await forecastService.resetEvents(); setResetConfirm(false); refetch() }
    catch { setResetConfirm(false) }
  }
  const toggleActive = async (e) => {
    await forecastService.updateEvent(e.id, { is_active: !e.is_active }); refetch()
  }

  if (isLoading) return <div className="flex justify-center py-16"><Spinner/></div>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-400">
          {events.length} events drive the Prophet model. Add elections, once they're
          announced, or any promotion day you expect to move volume.
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => setResetConfirm(true)}
            className="px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white border border-surface-border flex items-center gap-2">
            <RotateCcw size={14}/> Restore SA defaults
          </button>
          <button onClick={() => { setErr(''); setModal('new') }}
            className="px-3 py-2 rounded-lg text-sm font-medium bg-primary text-white flex items-center gap-2">
            <Plus size={14}/> Add event
          </button>
        </div>
      </div>

      {events.length === 0 ? (
        <EmptyState icon={Calendar} title="No forecast events"
          message="Add SA holidays and voting days, or restore the defaults." />
      ) : EVENT_CATEGORIES.filter(c => grouped[c.value]?.length).map(c => {
        const I = c.icon
        return (
          <div key={c.value} className="bg-surface-card border border-surface-border rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-surface-border flex items-center gap-2">
              <I size={14} style={{ color: c.color }} />
              <h3 className="text-sm font-semibold text-white">{c.label}</h3>
              <span className="text-xs text-gray-500">({grouped[c.value].length})</span>
            </div>
            <div className="divide-y divide-surface-border">
              {grouped[c.value].map(e => (
                <div key={e.id} className={`px-4 py-2.5 flex items-center gap-3 ${e.is_active ? '' : 'opacity-45'}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate">{e.name}</p>
                    <p className="text-xs text-gray-500">
                      {whenLabel(e)} · window {e.lower_window}/{e.upper_window}
                      {e.note ? ` · ${e.note}` : ''}
                    </p>
                  </div>
                  <button onClick={() => toggleActive(e)} title={e.is_active ? 'Disable' : 'Enable'}
                    className={`text-xs px-2 py-1 rounded-lg border ${e.is_active ? 'border-green-400/30 text-green-400' : 'border-surface-border text-gray-500'}`}>
                    {e.is_active ? 'Active' : 'Off'}
                  </button>
                  <button onClick={() => { setErr(''); setModal(e) }} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white"><Pencil size={14}/></button>
                  <button onClick={() => setConfirm(e)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400"><Trash2 size={14}/></button>
                </div>
              ))}
            </div>
          </div>
        )
      })}

      <Modal open={!!modal} onClose={() => setModal(null)}
        title={modal === 'new' ? 'Add forecast event' : 'Edit forecast event'}>
        {err && <div className="mb-3 text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2">{err}</div>}
        <EventForm initial={modal === 'new' ? null : modal}
          onSave={save} onCancel={() => setModal(null)} saving={saving} />
      </Modal>

      <ConfirmDialog open={!!confirm} onClose={() => setConfirm(null)} onConfirm={doDelete}
        title="Delete event?" danger confirmLabel="Delete"
        message={`Remove "${confirm?.name}" from the forecast calendar? This can't be undone (use the Active toggle to disable instead).`} />

      <ConfirmDialog open={resetConfirm} onClose={() => setResetConfirm(false)} onConfirm={doReset}
        title="Restore SA defaults?" danger confirmLabel="Restore"
        message="This replaces the entire calendar with the built-in South African default set. Any custom events you added will be removed." />
    </div>
  )
}

function PromotionHistory() {
  const [open, setOpen] = useState(false)
  const { data, isLoading } = useQuery({
    queryKey: ['promo-performance'],
    queryFn: () => reportService.getPromotionPerformance(),
    staleTime: 60000,
  })
  const promos = Array.isArray(data) ? data : []
  return (
    <div className="bg-surface-card border border-surface-border rounded-xl overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors">
        <span className="flex items-center gap-2 text-sm font-semibold text-white">
          <Megaphone size={16} className="text-primary"/> Promotion History
          <span className="text-xs text-gray-500 font-normal">({promos.length})</span>
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}/>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-surface-border pt-3">
          <p className="text-xs text-gray-500 mb-3">How past promotions performed — reach, engagement and redemptions. Build your next promotion on the ones that worked.</p>
          {isLoading ? (
            <div className="flex justify-center py-4"><Spinner size={18}/></div>
          ) : promos.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">No promotions yet — create one on the Promotions page.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-gray-500 text-xs">
                  <tr>
                    <th className="py-2 pr-3 font-medium text-left">Promotion</th>
                    <th className="py-2 px-2 font-medium text-right">Reach</th>
                    <th className="py-2 px-2 font-medium text-right">Views</th>
                    <th className="py-2 px-2 font-medium text-right">View %</th>
                    <th className="py-2 px-2 font-medium text-right">Redeemed</th>
                    <th className="py-2 px-2 font-medium text-right">Conv %</th>
                    <th className="py-2 pl-2 font-medium text-right">Units</th>
                  </tr>
                </thead>
                <tbody>
                  {promos.map(p => (
                    <tr key={p.id} className="border-t border-[#222]">
                      <td className="py-2 pr-3 text-gray-200">
                        {p.title}
                        {p.is_active && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-green-400/10 text-green-400">live</span>}
                      </td>
                      <td className="py-2 px-2 text-right text-gray-300">{fmtVal(p.reach)}</td>
                      <td className="py-2 px-2 text-right text-gray-300">{fmtVal(p.views)}</td>
                      <td className="py-2 px-2 text-right text-gray-400">{p.view_rate}%</td>
                      <td className="py-2 px-2 text-right text-gray-300">{fmtVal(p.redemptions)}</td>
                      <td className="py-2 px-2 text-right text-primary font-medium">{p.conversion}%</td>
                      <td className="py-2 pl-2 text-right text-gray-300">{fmtVal(p.units)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function Forecasting() {
  const [tab, setTab] = useState('business')
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <TrendingUp size={22} className="text-primary"/> Forecasting
        </h1>
        <p className="text-gray-400 text-sm mt-0.5">Meta Prophet · SA public holidays · Liquor seasonality · Stock runway</p>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-black/30 border border-surface-border rounded-xl p-1 w-fit">
        {TABS.map(t => { const I = t.icon; return (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab===t.key?'bg-primary text-white':'text-gray-400 hover:text-gray-200'}`}>
            <I size={14}/> {t.label}
          </button>
        )})}
      </div>

      {tab === 'business' ? <BusinessTab/> : tab === 'product' ? <ProductTab/> : <CalendarTab/>}

      <PromotionHistory/>

      <div className="bg-black/30 border border-[#2a2a2a] rounded-xl p-4 flex gap-3">
        <Info size={16} className="text-gray-500 flex-shrink-0 mt-0.5"/>
        <p className="text-xs text-gray-500 leading-relaxed">
          <span className="text-gray-400 font-medium">About this forecast: </span>
          Powered by Meta Prophet with all 12 South African public holidays encoded — Heritage Day (Braai Day), Christmas, Easter, Freedom Day, Workers Day, Youth Day, Women's Day, Day of Reconciliation, Human Rights Day, and election alcohol bans. A custom SA liquor seasonality component captures the December–January festive surge and mid-winter slowdown. Election ban dates are modelled as point anomalies so surrounding demand is unaffected. Confidence intervals represent an 80% prediction band.
        </p>
      </div>
    </div>
  )
}
